import * as React from 'react';

import { AutoMessageContentInput } from 'globalTypes';

import ComposerTextboxWithTokens from 'chat/components/ComposerTextboxWithTokens';
// @ts-ignore
import { fetchFileByIdRequest } from 'clients/api/fileUploads';
import FormElement from 'components/FormElement';
import Link from 'components/Link';
// @ts-ignore
import Notice from 'components/Notice';
import Row from 'components/Row';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import TranslateButton from 'components/TranslateButton';
import LinkConstants from 'constants/LinkConstants';
// TODO: once ComposerFilePreview is updated and doesn't need a `File`,
// we can remove this
import File from 'entities/File';
import assertions from 'lib/assertions';

type Props = {
  hasCustomizedMessaging: boolean;
  handleMessageContentChange: (
    message: Partial<AutoMessageContentInput> | undefined,
    valid: boolean
  ) => void;
  handleCustomMessageFieldsChange: (tokens: string[]) => void;
  onTokenIncrease: () => void;
  value?: string;
  fileUuid?: string;
  isUrgent?: boolean;
};

type State = {
  uploadedFile?: File;
};

export default class AdminNewAutoMessageComposeTextarea extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.messageContentTextarea = React.createRef();

    this.state = {};
  }

  componentDidMount() {
    if (this.props.fileUuid) {
      this.fetchFileIntoState();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.fileUuid === this.props.fileUuid) {
      return;
    }

    if (this.props.fileUuid) {
      this.fetchFileIntoState();
      return;
    }

    this.setState({ uploadedFile: undefined });
  }

  fetchFileIntoState = async () => {
    const uploadedFile = await fetchFileByIdRequest(this.props.fileUuid);
    this.setState({ uploadedFile });
  };

  messageContentTextarea: React.RefObject<FormElement>;

  handleFileUpload = (fileUuid?: string) => {
    const fileAttachmentUuids = fileUuid ? [fileUuid] : [];
    this.props.handleMessageContentChange({ fileAttachmentUuids }, true);
  };

  handleTokenChange = (tokens: string[]) => this.props.handleCustomMessageFieldsChange(tokens);

  render() {
    const {
      hasCustomizedMessaging,
      handleMessageContentChange,
      onTokenIncrease,
      value,
      isUrgent,
    } = this.props;

    // Since customized messaging is currently behind a feature flag,
    // and ComposerTextboxWithTokens just takes a value directly (not a SyntheticEvent),
    // we need to differentiate between whether a user has customized messaging
    // turned on and handle all text change events accordingly
    const onTextChange = (e: any, valid: boolean) => {
      handleMessageContentChange({ body: hasCustomizedMessaging ? e : e.target.value }, valid);
    };

    const onTranslate = (text: string) => {
      if (!this.messageContentTextarea) {
        console.error(
          "AdminNewAutoMessageCompose - tried to translate but couldn't fetch messageContentTextarea"
        );
        return;
      }
      this.messageContentTextarea.current!.handleChange({ target: { value: text } });
    };

    return (
      <FormElement
        data-test="message-content-textarea"
        ref={this.messageContentTextarea}
        label="Message content"
        labelNote="Recipients will receive this auto message as text messages, push notifications, or
        emails, depending on their Remind notification preferences."
        onValidChange={e => onTextChange(e, true)}
        onInvalidChange={e => onTextChange(e, false)}
        subtext={
          <React.Fragment>
            {!hasCustomizedMessaging && (
              <Row size="xsmall">
                <TranslateButton
                  textToTranslate={value ?? ''}
                  onTextChange={onTranslate}
                  label="Translate message content"
                />
              </Row>
            )}
            {hasCustomizedMessaging && (
              <Row size="small">
                <Notice color="polar-bear" noShadow>
                  <Text weight="semibold">TIP:</Text> To add custom data fields to this message,
                  enter them with the format <Text element="code">{`{{first_name}}`}</Text>.{' '}
                  <Link href={LinkConstants.CUSTOMIZED_MESSAGING_HELP} target="_blank">
                    Learn more
                  </Link>
                </Notice>
              </Row>
            )}
          </React.Fragment>
        }
        validate={e => {
          const value = hasCustomizedMessaging ? e : e.target.value;
          assertions.notEmpty(value, 'Messages must not be empty.');
          assertions.lengthLesserThanOrEqualTo(
            value,
            600,
            'Messages must be 600 characters or less.'
          );
        }}
        element={hasCustomizedMessaging ? ComposerTextboxWithTokens : Textarea}
        elementProps={{
          value,
          placeholder: 'Type your message',
          onTokenChange: this.handleTokenChange,
          onTokenIncrease,
          onFileUpload: this.handleFileUpload,
          fileUpload: this.state.uploadedFile,
          isUrgent,
        }}
      />
    );
  }
}
